import React, { useState } from "react";
import {
  IoPersonOutline,
  IoCardOutline,
  IoCalendarOutline,
  IoCallOutline,
  IoMailOutline,
  IoLocationOutline,
  IoBriefcaseOutline,
  IoPeopleOutline,
  IoBusinessOutline,
  IoPencilOutline,
  IoSaveOutline,
  IoCloseOutline,
  IoChevronBackOutline,
} from "react-icons/io5";

export default function OccupantDetails({ occupant, setViewMode, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(occupant);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onUpdate) {
      await onUpdate(formData);
    }
    setIsEditing(false);
  };

  const genderOptions = ["Male", "Female", "Other"];
  const civilStatusOptions = ["Single", "Married", "Divorced", "Widowed"];

  const SectionHeader = ({ icon: Icon, title }) => (
    <div className="flex items-center gap-2 pb-2 mb-3 border-b border-[#363062]/10">
      <div className="p-1.5 rounded-lg bg-[#363062]/5">
        <Icon className="w-4 h-4 text-[#363062]" />
      </div>
      <h3 className="text-sm font-medium text-[#363062]">{title}</h3>
    </div>
  );

  const InfoField = ({ icon: Icon, label, value }) => (
    <div className="bg-[#363062]/5 p-3 rounded-lg space-y-1">
      <div className="flex items-center gap-2 text-[#363062]/70 text-xs font-medium">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
      <p className="text-[#363062] pl-6 break-words text-sm">
        {value || "Not provided"}
      </p>
    </div>
  );

  const InputField = ({ icon: Icon, label, ...props }) => (
    <div className="bg-white p-3 rounded-lg space-y-2 border border-[#363062]/10">
      <div className="flex items-center gap-2 text-[#363062]/70 text-xs font-medium">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
      <input
        className="w-full pl-6 pr-3 py-2 text-sm border border-[#363062]/20 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] bg-white text-[#363062]"
        {...props}
      />
    </div>
  );

  const SelectField = ({ icon: Icon, label, options, ...props }) => (
    <div className="bg-white p-3 rounded-lg space-y-2 border border-[#363062]/10">
      <div className="flex items-center gap-2 text-[#363062]/70 text-xs font-medium">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
      <select
        className="w-full pl-6 pr-3 py-2 text-sm border border-[#363062]/20 rounded-lg focus:ring-2 focus:ring-[#8FCD00]/20 focus:border-[#8FCD00] bg-white text-[#363062]"
        {...props}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-white overflow-y-auto z-50">
      {/* Header */}
      <div className="sticky top-0 pt-16 z-[51] bg-white border-b border-[#363062]/10 px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <button
              className="p-2 hover:bg-[#363062]/5 rounded-full transition-colors"
              onClick={() => setViewMode(false)}
            >
              <IoChevronBackOutline className="w-5 h-5 text-[#363062]" />
            </button>
          </div>
          {!isEditing ? (
            <button
              className="flex items-center gap-1 px-3 py-1.5 bg-[#8FCD00] text-white rounded-full text-sm hover:bg-[#8FCD00]/90 transition-colors"
              onClick={() => setIsEditing(true)}
            >
              <IoPencilOutline className="w-4 h-4" />
              <span>Edit</span>
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                className="flex items-center gap-1 px-3 py-1.5 bg-[#8FCD00] text-white rounded-full text-sm hover:bg-[#8FCD00]/90 transition-colors"
                onClick={handleSubmit}
              >
                <IoSaveOutline className="w-4 h-4" />
                <span>Save</span>
              </button>
              <button
                className="flex items-center gap-1 px-3 py-1.5 bg-[#363062]/10 text-[#363062] rounded-full text-sm hover:bg-[#363062]/20 transition-colors"
                onClick={() => {
                  setFormData(occupant);
                  setIsEditing(false);
                }}
              >
                <IoCloseOutline className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          )}
        </div>
        <div className="px-2">
          <h2 className="text-lg font-semibold text-[#363062]">
            {formData.firstName} {formData.lastName}
          </h2>
          <p className="text-sm text-[#363062]/70">{formData.relationship}</p>
        </div>
      </div>

      {/* Content */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6 pb-20">
        {/* Personal Information */}
        <section>
          <SectionHeader icon={IoPersonOutline} title="Personal Information" />
          <div className="grid grid-cols-1 gap-3">
            {isEditing ? (
              <>
                <InputField
                  icon={IoPersonOutline}
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoPersonOutline}
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
                <InputField
                  icon={IoPersonOutline}
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoPeopleOutline}
                  label="Relationship"
                  name="relationship"
                  value={formData.relationship}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoCalendarOutline}
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                />
                <SelectField
                  icon={IoPersonOutline}
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  options={genderOptions}
                  onChange={handleChange}
                  required
                />
                <SelectField
                  icon={IoCardOutline}
                  label="Civil Status"
                  name="civilStatus"
                  value={formData.civilStatus}
                  options={civilStatusOptions}
                  onChange={handleChange}
                  required
                />
              </>
            ) : (
              <>
                <InfoField
                  icon={IoPersonOutline}
                  label="First Name"
                  value={formData.firstName}
                />
                <InfoField
                  icon={IoPersonOutline}
                  label="Middle Name"
                  value={formData.middleName}
                />
                <InfoField
                  icon={IoPersonOutline}
                  label="Last Name"
                  value={formData.lastName}
                />
                <InfoField
                  icon={IoPeopleOutline}
                  label="Relationship"
                  value={formData.relationship}
                />
                <InfoField
                  icon={IoCalendarOutline}
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                />
                <InfoField
                  icon={IoPersonOutline}
                  label="Gender"
                  value={formData.gender}
                />
                <InfoField
                  icon={IoCardOutline}
                  label="Civil Status"
                  value={formData.civilStatus}
                />
              </>
            )}
          </div>
        </section>

        {/* Contact Information */}
        <section>
          <SectionHeader icon={IoCallOutline} title="Contact Information" />
          <div className="grid grid-cols-1 gap-3">
            {isEditing ? (
              <>
                <InputField
                  icon={IoCallOutline}
                  label="Contact Number"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoMailOutline}
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoLocationOutline}
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </>
            ) : (
              <>
                <InfoField
                  icon={IoCallOutline}
                  label="Contact Number"
                  value={formData.contactNumber}
                />
                <InfoField
                  icon={IoMailOutline}
                  label="Email"
                  value={formData.email}
                />
                <InfoField
                  icon={IoLocationOutline}
                  label="Address"
                  value={formData.address}
                />
              </>
            )}
          </div>
        </section>

        {/* Employment Information */}
        <section>
          <SectionHeader
            icon={IoBriefcaseOutline}
            title="Employment Information"
          />
          <div className="grid grid-cols-1 gap-3">
            {isEditing ? (
              <>
                <InputField
                  icon={IoBriefcaseOutline}
                  label="Occupation"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                />
                <InputField
                  icon={IoBusinessOutline}
                  label="Employer"
                  name="employer"
                  value={formData.employer}
                  onChange={handleChange}
                />
              </>
            ) : (
              <>
                <InfoField
                  icon={IoBriefcaseOutline}
                  label="Occupation"
                  value={formData.occupation}
                />
                <InfoField
                  icon={IoBusinessOutline}
                  label="Employer"
                  value={formData.employer}
                />
              </>
            )}
          </div>
        </section>

        {/* Emergency Contact */}
        <section>
          <SectionHeader icon={IoCallOutline} title="Emergency Contact" />
          <div className="grid grid-cols-1 gap-3">
            {isEditing ? (
              <>
                <InputField
                  icon={IoPersonOutline}
                  label="Emergency Contact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoCallOutline}
                  label="Emergency Contact Number"
                  name="emergencyContactNumber"
                  value={formData.emergencyContactNumber}
                  onChange={handleChange}
                  required
                />
                <InputField
                  icon={IoPeopleOutline}
                  label="Emergency Contact Relationship"
                  name="emergencyContactRelationship"
                  value={formData.emergencyContactRelationship}
                  onChange={handleChange}
                  required
                />
              </>
            ) : (
              <>
                <InfoField
                  icon={IoPersonOutline}
                  label="Emergency Contact"
                  value={formData.emergencyContact}
                />
                <InfoField
                  icon={IoCallOutline}
                  label="Emergency Contact Number"
                  value={formData.emergencyContactNumber}
                />
                <InfoField
                  icon={IoPeopleOutline}
                  label="Emergency Contact Relationship"
                  value={formData.emergencyContactRelationship}
                />
              </>
            )}
          </div>
        </section>
      </form>
    </div>
  );
}